import React, { useEffect, useRef, useState } from 'react'
// No banner on this chart: it renders a bare <svg> with no controls row to
// host one. The window still falls back, so the data appears; the sibling
// TracPatch charts on the same page carry the explanation.
import { effectiveWindow } from './tracpatch.window'
import { Row, Button } from 'antd'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'

// Blue single-line chart of "# exercises completed per day". Replaces the old
// per-day compliance table in the Exercise VIEW modal. Dumb/self-contained:
// the caller passes already-aggregated daily data so the existing
// dedup/count logic in activities.js stays the single source of truth.
//
//   props.data = [{ date: Date|ISO string, done: number, total: number }]
//                sorted ascending by date. Plots `done` per day.

const TracPatchBlue = CHART_COLORS.lineBlue

const ExerciseCompletionLineChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)

  // Last `days` days of daily completions (default 30), matching the other
  // TracPatch charts' window controls.
  // Falls back to the patient's latest completion — see tracpatch.window.js.
  const win = effectiveWindow(props.data, days, (d) => d && d.date)
  const windowStart = win.windowStart.clone()
  const data = (props.data || [])
    .map((d) => ({ ...d, date: moment(d.date).startOf('day').toDate() }))
    .filter((d) => moment(d.date).isSameOrAfter(windowStart))
    // Upper bound matters now the window can end before today.
    .filter((d) => !moment(d.date).isAfter(win.windowEnd))
    .sort((a, b) => a.date - b.date)

  useEffect(() => {
    draw()
  }, [props.data, days])

  const draw = () => {
    if (data.length === 0) return

    const margin = { top: 20, right: 20, bottom: 60, left: 50 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3
      .scaleTime()
      .domain([data[0].date, data[data.length - 1].date])
      .range([0, width])

    const yMax = Math.max(1, d3.max(data, (d) => d.done))
    const y = d3.scaleLinear().domain([0, yMax + 1]).range([height, 0])

    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(Math.min(8, data.length)).tickFormat(d3.timeFormat('%m/%d')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', data.length > 20 ? '10px' : '11px')
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', '12px')
      .text('Date')

    g.append('g')
      .call(d3.axisLeft(y).ticks(Math.min(6, yMax + 1)).tickFormat(d3.format(',.0f')))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', '12px')
      .text('Exercises completed')

    const tooltip = d3.select(tooltipRef.current)

    const lineGen = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.done))
    g.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', TracPatchBlue)
      .attr('stroke-width', 2)
      .attr('d', lineGen)

    g.selectAll('.dot-done')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'dot-done')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.done))
      .attr('r', 4)
      .attr('fill', TracPatchBlue)
      .style('cursor', 'pointer')
      .on('mouseover', function (d) {
        tooltip
          .style('visibility', 'visible')
          .html(
            `<b>Date</b>: ${moment(d.date).format('MM/DD')}` +
            `<br/><b>Completed</b>: ${d.done}${d.total != null ? ' of ' + d.total : ''}`
          )
          .style('top', d3.select(this).attr('cy') - 50 + 'px')
          .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))

    const labelStep = data.length > 10 ? 2 : 1
    data.forEach((d, i) => {
      const fromEnd = data.length - 1 - i
      if (fromEnd % labelStep !== 0) return
      g.append('text')
        .attr('x', x(d.date))
        .attr('y', y(d.done) - 6)
        .attr('text-anchor', 'middle')
        .attr('fill', TracPatchBlue)
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text(String(d.done))
    })
  }

  return (
    <>
      <center>
        <h4>Exercises completed per day</h4>
      </center>
      <Row justify='center' style={{ marginBottom: 8 }}>
        <Button.Group size='small'>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
      </Row>
      {data.length > 0 ? (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
          <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
          <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
        </div>
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

export default ExerciseCompletionLineChart
